import { useEffect, useState } from "react";
import { StarDecorations } from "components/ui/StarDecorations";
import { atualizarEntrega, buscarEntregasPorGestorPorPrefixo, buscarIniciativasElbbDoGestor } from "services/entrega.service";
import { useAuth } from "context/AuthContext";
import { FiSearch } from "react-icons/fi";
import { FaCheck, FaTimes } from "react-icons/fa";
import { CardIniciativa } from "components/ui/CardIniciativa";
import { ModalEditarIniciativa } from "components/ui/ModalEditarIniciativa";

interface Entrega {
  _id: string;
  title: string;
  description: string;
  indicator: {
    nome: string;
    cod: string;
  };
  owner: string;
  owner_name: string;
  owner_name_guerra: string;
  owner_funcao: string;
  owner_nome_dependencia: string;
  owner_avatar: any;
  start: number;
  end: number;
  status: string;
  team: Array<{ _id: string; name?: string; code?: string }>;
  evaluator_detail: any[];
  non_evaluator_detail: any[];
  prefixo: number;
  diretoria: string;
  result: string;
  original_manager: string;
  original_manager_detail: any;
  mad: boolean;
  approval_log?: ApprovalLogEntry[];
  categoryPremio?: string;
}

interface ApprovalLogEntry {
  action: "aprovada" | "reprovada" | "aguardando" | "editada";
  user_id: string;
  user_name: string;
  user_matricula: string;
  timestamp: string;
}

export function ValidacaoIniciativas() {
  const [entregas, setEntregas] = useState<Entrega[]>([]);
  const [entregasFiltradas, setEntregasFiltradas] = useState<Entrega[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState<string>("todos");
  const [termoBusca, setTermoBusca] = useState<string>("");
  const [entregaEditando, setEntregaEditando] = useState<Entrega | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    async function carregarEntregas() {
      try {
        setLoading(true);
        // Conexão/Pilar são roteadas pelo prefixo; ELBB pelo gestor escolhido no cadastro (original_manager)
        const [porPrefixo, elbb] = await Promise.all([
          buscarEntregasPorGestorPorPrefixo({
            prefixo: String(user?.detail.prefixo ?? ""),
          }),
          buscarIniciativasElbbDoGestor(user?._id || ""),
        ]);
        const data = [...(porPrefixo || []), ...elbb];
        setEntregas(data);
        setEntregasFiltradas(data);
      } catch (error) {
        console.error("Erro ao carregar entregas:", error);
        setEntregas([]);
        setEntregasFiltradas([]);
      } finally {
        setLoading(false);
      }
    }

    carregarEntregas();
  }, [user]);

  useEffect(() => {
    let resultados = [...entregas];

    // Filtrar por status
    if (filtroStatus === "aprovadas") {
      resultados = resultados.filter((e) => e.status === "aprovada");
    } else if (filtroStatus === "reprovadas") {
      resultados = resultados.filter((e) => e.status === "reprovada");
    } else if (filtroStatus === "aguardando") {
      resultados = resultados.filter((e) => e.status === "aguardando");
    }

    // Filtrar por termo de busca
    if (termoBusca.trim()) {
      const termo = termoBusca.toLowerCase();
      resultados = resultados.filter(
        (e) =>
          e.title?.toLowerCase().includes(termo) ||
          e.indicator?.nome?.toLowerCase().includes(termo) ||
          e.owner_name?.toLowerCase().includes(termo)
      );
    }

    setEntregasFiltradas(resultados);
  }, [filtroStatus, termoBusca, entregas]);

  const handleAprovar = async (entregaId: string) => {
    try {
      const entrega = entregas.find((e) => e._id === entregaId);
      if (!entrega) return;

      const logEntry: ApprovalLogEntry = {
        action: "aprovada",
        user_id: user?._id || "",
        user_name: user?.detail?.nome_guerra || user?.detail?.nome || "",
        user_matricula: user?._id || "",
        timestamp: new Date().toISOString(),
      };
      const approval_log = [...(entrega.approval_log || []), logEntry];

      await atualizarEntrega({
        ...entrega,
        status: "aprovada",
        approval_log,
        updated: new Date().toISOString(),
      });

      // Atualizar o estado local
      setEntregas((prev) =>
        prev.map((e) =>
          e._id === entregaId ? { ...e, status: "aprovada", approval_log } : e
        )
      );
    } catch (error) {
      console.error("Erro ao aprovar iniciativa:", error);
    }
  };

  const handleReprovar = async (entregaId: string) => {
    try {
      const entrega = entregas.find((e) => e._id === entregaId);
      if (!entrega) return;

      const logEntry: ApprovalLogEntry = {
        action: "reprovada",
        user_id: user?._id || "",
        user_name: user?.detail?.nome_guerra || user?.detail?.nome || "",
        user_matricula: user?._id || "",
        timestamp: new Date().toISOString(),
      };
      const approval_log = [...(entrega.approval_log || []), logEntry];

      await atualizarEntrega({
        ...entrega,
        status: "reprovada",
        approval_log,
        updatedAt: new Date().toISOString(),
      });

      // Atualizar o estado local
      setEntregas((prev) =>
        prev.map((e) =>
          e._id === entregaId ? { ...e, status: "reprovada", approval_log } : e
        )
      );
    } catch (error) {
      console.error("Erro ao reprovar iniciativa:", error);
    }
  };

  const handleDesfazer = async (entregaId: string) => {
    try {
      const entrega = entregas.find((e) => e._id === entregaId);
      if (!entrega) return;

      const logEntry: ApprovalLogEntry = {
        action: "aguardando",
        user_id: user?._id || "",
        user_name: user?.detail?.nome_guerra || user?.detail?.nome || "",
        user_matricula: user?._id || "",
        timestamp: new Date().toISOString(),
      };
      const approval_log = [...(entrega.approval_log || []), logEntry];

      await atualizarEntrega({
        ...entrega,
        status: "aguardando",
        approval_log,
        updatedAt: new Date().toISOString(),
      });

      // Atualizar o estado local
      setEntregas((prev) =>
        prev.map((e) =>
          e._id === entregaId ? { ...e, status: "aguardando", approval_log } : e
        )
      );
    } catch (error) {
      console.error("Erro ao desfazer ação:", error);
    }
  };

  const handleEditar = (entregaId: string) => {
    const entrega = entregas.find((e) => e._id === entregaId);
    if (entrega) setEntregaEditando(entrega);
  };

  const handleSalvarEdicao = (entregaAtualizada: Entrega) => {
    setEntregas((prev) =>
      prev.map((e) => (e._id === entregaAtualizada._id ? entregaAtualizada : e))
    );
    setEntregaEditando(null);
  };

  const categoriaPredominante = entregas.length > 0 ? entregas[0].categoryPremio : undefined;

  const contadorStatus = {
    aprovadas: entregas.filter((e) => e.status === "aprovada").length,
    reprovadas: entregas.filter((e) => e.status === "reprovada").length,
    aguardando: entregas.filter((e) => e.status === "aguardando").length,
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-bb via-blue-bb-1 to-blue-bb-5 overflow-hidden relative">
      <StarDecorations count={18} density="medium" />

      <div className="container mx-auto px-6 py-12 max-w-6xl relative z-10">
        <div className="mb-8">
          <h1 className="text-green-bb-2 text-4xl font-bold mb-4">
            Valide as iniciativas
          </h1>
          <p className="text-white text-2xl">
            Confira as iniciativas {categoriaPredominante === "PILAR" ? "estruturais" : "negociais"} inscritas no Prêmio BB e valide as que foram fundamentais para a superação do indicador da Plataforma Conexão ao qual a iniciativa está vinculada. Não há um limite de validação de iniciativas.
          </p>
        </div>

        <div className="mb-6">
          <p className="text-white text-lg font-semibold mb-4">
            Total de iniciativas: {entregas.length}
          </p>

          {/* Filtros */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="relative">
              <select
                value={filtroStatus}
                onChange={(e) => setFiltroStatus(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="todos">Filtrar por status</option>
                <option value="aprovadas">Validadas</option>
                <option value="reprovadas">Reprovadas</option>
                <option value="aguardando">Aguardando</option>
              </select>
            </div>

            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="O que você procura?"
                value={termoBusca}
                onChange={(e) => setTermoBusca(e.target.value)}
                className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Layout com Contadores e Listagem */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Contadores - Coluna Esquerda */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg p-4 shadow-lg sticky top-6">
              <div className="flex items-center gap-2 mb-3">
                <FaCheck className="text-green-600" />
                <label className="text-gray-700 font-medium text-sm">
                  Validadas ({contadorStatus.aprovadas})
                </label>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <FaTimes className="text-red-600" />
                <label className="text-gray-700 font-medium text-sm">
                  Não validadas ({contadorStatus.reprovadas}/{entregas.length})
                </label>
              </div>
              <div className="flex items-center gap-2">
                <svg 
                  className="w-4 h-4 text-red-600" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" 
                  />
                </svg>
                <label className="text-gray-700 font-medium text-sm">
                  Aguardando ({contadorStatus.aguardando})
                </label>
              </div>
            </div>
          </div>

          {/* Listagem - Coluna Direita */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              {/* Lista de iniciativas */}
              {loading ? (
                <p className="text-gray-600 text-center text-lg py-8">
                  Carregando iniciativas...
                </p>
              ) : entregasFiltradas.length === 0 ? (
                <p className="text-gray-600 text-center text-lg py-8">
                  Nenhuma iniciativa encontrada.
                </p>
              ) : (
                <div className="space-y-6">
                  {entregasFiltradas.map((entrega) => (
                    <CardIniciativa
                      key={entrega._id}
                      entrega={entrega}
                      onAprovar={handleAprovar}
                      onReprovar={handleReprovar}
                      onDesfazer={handleDesfazer}
                      onEditar={handleEditar}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {entregaEditando && (
        <ModalEditarIniciativa
          entrega={entregaEditando}
          onSave={handleSalvarEdicao}
          onClose={() => setEntregaEditando(null)}
          currentUser={user}
        />
      )}
    </div>
  );
}
